import { Point } from "./bounds";

export type BaseDirections =
  | Directions.Up
  | Directions.Right
  | Directions.Down
  | Directions.Left;

export enum Directions {
  UpLeft,
  Up,
  UpRight,
  DownLeft,
  Down,
  DownRight,
  Left,
  Right,
}

export const allDirections = [
  Directions.Up,
  Directions.Down,
  Directions.Left,
  Directions.Right,
];

export const allDirectionWithDiagonals = [
  Directions.UpLeft,
  Directions.Up,
  Directions.UpRight,
  Directions.Left,
  Directions.Right,
  Directions.DownLeft,
  Directions.Down,
  Directions.DownRight,
];

export function getOppositeDirectionWithDiagonal(direction: Directions) {
  switch (direction) {
    case Directions.Up:
      return Directions.Down;
    case Directions.Left:
      return Directions.Right;
    case Directions.Down:
      return Directions.Up;
    case Directions.Right:
      return Directions.Left;
    case Directions.UpLeft:
      return Directions.DownRight;
    case Directions.UpRight:
      return Directions.DownLeft;
    case Directions.DownRight:
      return Directions.UpLeft;
  }

  ((): never => {
    throw new Error("Unrecognized direction");
  })();
}

export function getOppositeDirection(direction: Directions) {
  switch (direction) {
    case Directions.Up:
      return Directions.Down;
    case Directions.Left:
      return Directions.Right;
    case Directions.Down:
      return Directions.Up;
    case Directions.Right:
      return Directions.Left;
  }

  ((): never => {
    throw new Error("Unrecognized direction");
  })();
}

export function getInDirection(position: Point, direction: Directions): Point {
  switch (direction) {
    case Directions.Up:
      return { x: position.x, y: position.y - 1 };
    case Directions.Left:
      return { x: position.x - 1, y: position.y };
    case Directions.Down:
      return { x: position.x, y: position.y + 1 };
    case Directions.Right:
      return { x: position.x + 1, y: position.y };
    case Directions.UpLeft:
      return { x: position.x - 1, y: position.y - 1 };
    case Directions.UpRight:
      return { x: position.x + 1, y: position.y - 1 };
    case Directions.DownLeft:
      return { x: position.x - 1, y: position.y + 1 };
    case Directions.DownRight:
      return { x: position.x + 1, y: position.y + 1 };
  }

  ((): never => {
    throw new Error("Unrecognized direction");
  })();
}

// TODO: This function can throw if source === target
export function getDirectionTowards(source: Point, target: Point) {
  if (source.x > target.x) {
    return Directions.Left;
  } else if (source.x < target.x) {
    return Directions.Right;
  } else if (source.y > target.y) {
    return Directions.Up;
  } else if (source.y < target.y) {
    return Directions.Down;
  } else {
    throw new Error("Unrecognized direction");
  }
}

export function getDirectionFromVector(vector: Point) {
  if (vector.x < 0) {
    return vector.y === 0
      ? Directions.Left
      : vector.y < 0
      ? Directions.UpLeft
      : Directions.DownLeft;
  } else if (vector.x > 0) {
    return vector.y === 0
      ? Directions.Right
      : vector.y < 0
      ? Directions.UpRight
      : Directions.DownRight;
  } else if (vector.y < 0) {
    return Directions.Up;
  } else if (vector.y > 0) {
    return Directions.Down;
  }
}

export function getAllDirections(position: Point) {
  return [
    { x: position.x, y: position.y - 1 },
    { x: position.x - 1, y: position.y },
    { x: position.x, y: position.y + 1 },
    { x: position.x + 1, y: position.y },
  ];
}

export function getAllWithDiagonal(position: Point) {
  return [
    { x: position.x, y: position.y - 1 },
    { x: position.x - 1, y: position.y - 1 },
    { x: position.x - 1, y: position.y },
    { x: position.x - 1, y: position.y + 1 },
    { x: position.x, y: position.y + 1 },
    { x: position.x + 1, y: position.y + 1 },
    { x: position.x + 1, y: position.y },
    { x: position.x + 1, y: position.y - 1 },
  ];
}

export function* genAllDirections(position: Point): IterableIterator<Point> {
  yield { x: position.x, y: position.y - 1 };
  yield { x: position.x - 1, y: position.y };
  yield { x: position.x, y: position.y + 1 };
  yield { x: position.x + 1, y: position.y };
}

export function* genAllWithDiagonal(position: Point): IterableIterator<Point> {
  yield { x: position.x, y: position.y - 1 };
  yield { x: position.x - 1, y: position.y - 1 };
  yield { x: position.x - 1, y: position.y };
  yield { x: position.x - 1, y: position.y + 1 };
  yield { x: position.x, y: position.y + 1 };
  yield { x: position.x + 1, y: position.y + 1 };
  yield { x: position.x + 1, y: position.y };
  yield { x: position.x + 1, y: position.y - 1 };
}

export function* genAllDirectionsWithInfo(
  position: Point
): IterableIterator<[BaseDirections, Point]> {
  yield [Directions.Up, { x: position.x, y: position.y - 1 }];
  yield [Directions.Left, { x: position.x - 1, y: position.y }];
  yield [Directions.Down, { x: position.x, y: position.y + 1 }];
  yield [Directions.Right, { x: position.x + 1, y: position.y }];
}
